/*
 * /courses/:slug — xem trước một khoá (công khai, được index).
 * ?class=<slug> + đã đăng nhập → vào thẳng lớp đó.
 */
import { useEffect, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Icon } from "@/components/icon"
import { EmptyState, LoadingState, Page, PageHeader, usePageTitle } from "@/components/page"
import { CourseCodeForm } from "@/components/CourseCodeForm"
import { EditorialVisual } from "@/components/simba"
import { useSession } from "@/app/session"
import { api, loginUrl } from "@/lib/api"

export default function CoursePreviewPage() {
  const { slug = "" } = useParams()
  const [params] = useSearchParams()
  const classSlug = params.get("class")
  const { user } = useSession()
  const [data, setData] = useState<any>()
  const [error, setError] = useState("")
  const course = data?.course

  usePageTitle(
    course?.name || "Khóa học",
    course?.description || (course ? `Khám phá khóa học ${course.name} tại Simba — học thực hành cùng mentor Sư Tử Con.` : "Xem trước khóa học tại Simba Learner Space."),
    { noIndex: false },
  )

  useEffect(() => {
    if (user && classSlug) location.replace(`/classes/${encodeURIComponent(classSlug)}`)
  }, [user, classSlug])
  useEffect(() => {
    setData(undefined)
    setError("")
    api(`/api/public/courses/${encodeURIComponent(slug)}`)
      .then(setData)
      .catch((e) => setError(e.message))
  }, [slug])

  const back = (
    <Button variant="ghost" size="sm" className="self-start" nativeButton={false} render={<Link to={user ? "/my-courses" : "/"} />}>
      <Icon name="arrow-left" /> {user ? "Các khóa học" : "Tất cả khóa học"}
    </Button>
  )

  if (error)
    return (
      <Page>
        {back}
        <EmptyState mascot="giraffe-thinking" title="Không thể mở khóa học" description={error} />
      </Page>
    )
  if (!course) return <LoadingState label="Đang tải khóa học…" />

  const inClass = !!(classSlug && user)
  return (
    <Page>
      {back}
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
        <div className="flex flex-col gap-6">
          <PageHeader
            eyebrow="Tổng quan khóa học"
            title={course.name}
            description={course.description || "Khóa học thực hành với mentor đồng hành trong suốt hành trình."}
          />
          <Card>
            <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-soft">
                <Icon name={inClass ? "graduation" : "user"} className="size-7" />
              </span>
              <span className="flex-1 font-heading text-lg font-semibold text-ink">
                {inClass ? "Con đang học khóa này" : user ? "Con muốn tham gia khóa học?" : "Đăng nhập để bắt đầu học"}
              </span>
              {inClass ? (
                <Button nativeButton={false} render={<a href={`/classes/${encodeURIComponent(classSlug!)}`} />}>
                  Vào lớp <Icon name="arrow-right" />
                </Button>
              ) : (
                !user && (
                  <Button nativeButton={false} render={<a href={loginUrl(location.href)} />}>
                    Đăng nhập bằng Gmail <Icon name="arrow-right" />
                  </Button>
                )
              )}
            </CardContent>
          </Card>
          {user && (
            <Card size="sm">
              <CardContent>
                <Collapsible>
                  <CollapsibleTrigger className="flex w-full items-center gap-3 text-left font-heading text-base font-semibold text-ink">
                    <Icon name="lock" className="size-5" /> Nhập mã lớp
                    <Icon name="chevron-down" className="ml-auto size-4 text-muted-foreground" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <CourseCodeForm user={user} courseName={course.name} compact />
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          )}
        </div>
        <EditorialVisual kind="journey" alt={`Hành trình học ${course.name}`} priority className="hidden lg:block" />
      </div>
    </Page>
  )
}
